import { api } from '../services/authService';

export const S3_BROWSER_BLOCK_ERROR = 'S3_BROWSER_DIRECT_UPLOAD_FAILED';

/** S3 rejects a single presigned POST above 5 GiB. Larger browser uploads use multipart UploadPart. */
export const S3_SINGLE_POST_MAX_BYTES = 5 * 1024 * 1024 * 1024 - 1;

const MULTIPART_CONCURRENCY = 4;
const MULTIPART_URL_BATCH = 32;

/** @returns {Promise<{ post_url: string, form_fields: object, s3_path: string }>} */
export async function postUploadPresign(apiPath, file, organization, headers) {
  const formData = new FormData();
  formData.append('organization', organization);
  formData.append('file_name', file.name || 'upload');
  if (file.type) {
    formData.append('content_type', file.type);
  }
  const res = await api.post(apiPath, formData, { headers });
  return res.data;
}

/**
 * Multipart form POST to S3 using presigned post policy (bypasses app CDN; browser talks to S3 only).
 * The `file` part must be last, per S3 form POST.
 * @param {string} postUrl
 * @param {Record<string, string>} formFields hidden fields from the presign response
 * @param {File} file
 * @returns {Promise<void>}
 */
export async function postFileToS3PresignedPost(postUrl, formFields, file) {
  const form = new FormData();
  Object.keys(formFields).forEach((k) => {
    form.append(k, formFields[k]);
  });
  form.append('file', file, file.name || 'upload');
  let res;
  try {
    res = await fetch(postUrl, {
      method: 'POST',
      body: form,
    });
  } catch (e) {
    const n = e?.name || '';
    const msg = String(e?.message || e || '');
    if (n === 'TypeError' || n === 'NetworkError' || /NetworkError|failed to fetch|load failed|fetch resource/i.test(msg)) {
      throw new Error(S3_BROWSER_BLOCK_ERROR);
    }
    throw e;
  }
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(
      `S3 upload failed (${res.status} ${res.statusText})${t ? `: ${t.slice(0, 300)}` : ''}`
    );
  }
}

const S3_CORS_OR_BLOCKED_MESSAGE =
  'The browser could not complete the direct upload to S3. Check CORS on *-cube-file-uploads: allow Methods POST, PUT, GET, HEAD, your exact page origin, and AllowedHeaders * (form POST needs OPTIONS preflight). Confirm the API AWS region matches the bucket. Or use the S3 path field if uploads must bypass the browser.';

/** Extra hint when browser → S3 PUT fails (CORS, bad signature, etc.). */
export function formatS3DirectUploadError(error) {
  if (!error) return null;
  const m = String(error.message || '');
  if (m === S3_BROWSER_BLOCK_ERROR) {
    return S3_CORS_OR_BLOCKED_MESSAGE;
  }
  if (m.startsWith('S3 upload failed') || m.includes('S3 upload failed (')) {
    return `${m} Check S3 CORS (POST to bucket), bucket policy, and IAM; or use the S3 path field.`;
  }
  // Raw fetch failures we did not re-wrap (e.g. older code paths)
  if (
    error.name === 'NetworkError' ||
    (error.name === 'TypeError' &&
      (m === 'Failed to fetch' || m.toLowerCase().includes('load failed'))) ||
    /NetworkError when attempting to fetch|failed to fetch/i.test(m)
  ) {
    return S3_CORS_OR_BLOCKED_MESSAGE;
  }
  return null;
}

function throwIfBrowserBlocked(error) {
  const n = error?.name || '';
  const msg = String(error?.message || error || '');
  if (n === 'TypeError' || n === 'NetworkError' || /NetworkError|failed to fetch|load failed|fetch resource/i.test(msg)) {
    throw new Error(S3_BROWSER_BLOCK_ERROR);
  }
  throw error;
}

async function mapPool(items, limit, fn) {
  const results = new Array(items.length);
  let next = 0;
  async function worker() {
    while (next < items.length) {
      const i = next;
      next += 1;
      results[i] = await fn(items[i], i);
    }
  }
  const workers = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

async function putS3Part(url, blob) {
  let res;
  try {
    res = await fetch(url, { method: 'PUT', body: blob });
  } catch (e) {
    throwIfBrowserBlocked(e);
  }
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(
      `S3 upload failed (${res.status} ${res.statusText})${t ? `: ${t.slice(0, 300)}` : ''}`
    );
  }
  const etag = res.headers.get('ETag') || res.headers.get('etag');
  if (!etag) {
    throw new Error('S3 upload failed: missing ETag. Bucket CORS must expose ETag.');
  }
  return etag;
}

async function uploadFileViaMultipart({ uploadType, file, organization, headers }) {
  const startForm = new FormData();
  startForm.append('organization', organization);
  startForm.append('file_name', file.name || 'upload');
  startForm.append('file_size', String(file.size));
  startForm.append('upload_type', uploadType);
  if (file.type) startForm.append('content_type', file.type);
  const startRes = await api.post('/api/v2/upload_multipart_start', startForm, { headers });
  const { upload_id, s3_key, s3_path, part_size, part_count } = startRes.data;
  const partSize = part_size;
  try {
    const completed = [];
    for (let start = 1; start <= part_count; start += MULTIPART_URL_BATCH) {
      const numbers = [];
      for (let n = start; n < start + MULTIPART_URL_BATCH && n <= part_count; n += 1) {
        numbers.push(n);
      }
      const urlForm = new FormData();
      urlForm.append('organization', organization);
      urlForm.append('upload_type', uploadType);
      urlForm.append('s3_key', s3_key);
      urlForm.append('upload_id', upload_id);
      urlForm.append('part_numbers', numbers.join(','));
      const urlRes = await api.post('/api/v2/upload_multipart_parts', urlForm, { headers });
      const urlByNumber = {};
      (urlRes.data.parts || []).forEach((part) => {
        urlByNumber[part.part_number] = part.url;
      });
      const uploaded = await mapPool(numbers, MULTIPART_CONCURRENCY, async (n) => {
        const begin = (n - 1) * partSize;
        const blob = file.slice(begin, Math.min(begin + partSize, file.size));
        const etag = await putS3Part(urlByNumber[n], blob);
        return { part_number: n, etag };
      });
      completed.push(...uploaded);
    }
    const completeForm = new FormData();
    completeForm.append('organization', organization);
    completeForm.append('upload_type', uploadType);
    completeForm.append('s3_key', s3_key);
    completeForm.append('upload_id', upload_id);
    completeForm.append('parts', JSON.stringify(completed));
    await api.post('/api/v2/upload_multipart_complete', completeForm, { headers });
    return s3_path;
  } catch (error) {
    const abortForm = new FormData();
    abortForm.append('organization', organization);
    abortForm.append('upload_type', uploadType);
    abortForm.append('s3_key', s3_key);
    abortForm.append('upload_id', upload_id);
    await api.post('/api/v2/upload_multipart_abort', abortForm, { headers }).catch(() => {});
    throw error;
  }
}

/**
 * Upload a browser File to the cube uploads bucket and return its s3_path.
 * Uses a presigned POST up to S3's 5 GiB single-POST limit, then multipart UploadPart.
 * @param {{ presignPath: string, uploadType: 'universe'|'fast_id'|'bulk', file: File, organization: string, headers: object }} args
 * @returns {Promise<{ s3_path: string }>}
 */
export async function uploadBrowserFileToS3({ presignPath, uploadType, file, organization, headers }) {
  if (file.size <= S3_SINGLE_POST_MAX_BYTES) {
    const { post_url, form_fields, s3_path } = await postUploadPresign(
      presignPath,
      file,
      organization,
      headers
    );
    await postFileToS3PresignedPost(post_url, form_fields, file);
    return { s3_path };
  }
  const s3_path = await uploadFileViaMultipart({ uploadType, file, organization, headers });
  return { s3_path };
}
